import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { SITE, PROJECT_TYPES } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project_type: PROJECT_TYPES[0],
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Required";
    if (form.company.trim().length < 2) next.company = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Valid email required";
    if (form.message.trim().length < 10) next.message = "Tell us a bit more (10+ chars)";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        project_type: form.project_type,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Submission received. We'll respond within 48 hours.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try again or call our office.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HEADER */}
      <section className="bg-navy text-white">
        <div className="container-pro pt-24 pb-20 md:pt-32 md:pb-28">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue-400">
            08 / CONTACT
          </span>
          <h1 className="mt-6 font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
            Send us a scope.
            <br />
            We’ll respond within 48 hours.
          </h1>
          <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
            Whether it’s a single drafting package or a full FAT/SAT
            commissioning effort, we’ll prepare a deliverables matrix,
            schedule, and pricing tailored to your contract.
          </p>
        </div>
      </section>

      {/* SPLIT */}
      <section className="bg-white border-b border-hairline">
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12">
          {/* INFO */}
          <aside className="lg:col-span-5 bg-surface-alt p-8 md:p-14 lg:py-20 border-r border-hairline">
            <span className="overline">Field office</span>
            <h2 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-navy tracking-tight">
              Contact information
            </h2>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-md">
              Project managers monitor the inbox during operating hours.
              After-hours submissions are reviewed first thing the following
              business day.
            </p>

            <ul className="mt-10 space-y-6">
              <li className="flex gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-navy text-white shrink-0">
                  <Mail className="h-4 w-4" />
                </span>
                <div>
                  <div className="overline">Email</div>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-info-email"
                    className="mt-1 block font-heading font-semibold text-navy break-all"
                  >
                    {SITE.email}
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-navy text-white shrink-0">
                  <MapPin className="h-4 w-4" />
                </span>
                <div>
                  <div className="overline">Office</div>
                  <p className="mt-1 font-heading font-semibold text-navy">
                    {SITE.address}
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-navy text-white shrink-0">
                  <Clock className="h-4 w-4" />
                </span>
                <div>
                  <div className="overline">Technical Support Hours</div>
                  <p className="mt-1 font-heading font-semibold text-navy">
                    {SITE.hours}
                  </p>
                </div>
              </li>
            </ul>

            <div className="mt-12 pt-8 border-t border-hairline">
              <div className="overline">Registry</div>
              <p className="mt-3 text-sm text-slate-600 font-mono">
                {SITE.registry}
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7 p-8 md:p-14 lg:py-20">
            <span className="overline">Request a quote</span>
            <h2 className="mt-4 font-heading font-bold text-2xl md:text-3xl text-navy tracking-tight">
              Project intake
            </h2>

            {submitted ? (
              <div
                data-testid="contact-success"
                className="mt-10 p-8 border border-navy bg-navy text-white"
              >
                <div className="overline !text-slateblue-400">
                  Submission #{Math.random().toString(36).slice(2, 8).toUpperCase()}
                </div>
                <h3 className="mt-4 font-heading font-bold text-2xl">
                  Thank you — your scope has been received.
                </h3>
                <p className="mt-4 text-sm text-white/75 leading-relaxed max-w-md">
                  A project manager will respond within 48 business hours with
                  a deliverables matrix and follow-up questions.
                </p>
                <button
                  type="button"
                  data-testid="contact-reset"
                  onClick={() => setSubmitted(false)}
                  className="mt-8 link-underline !text-white"
                >
                  Submit another scope
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6"
                noValidate
              >
                <Field
                  label="Full name"
                  testId="contact-name"
                  value={form.name}
                  onChange={onChange("name")}
                  error={errors.name}
                  required
                />
                <Field
                  label="Company"
                  testId="contact-company"
                  value={form.company}
                  onChange={onChange("company")}
                  error={errors.company}
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  testId="contact-email"
                  value={form.email}
                  onChange={onChange("email")}
                  error={errors.email}
                  required
                />
                <Field
                  label="Phone (optional)"
                  type="tel"
                  testId="contact-phone"
                  value={form.phone}
                  onChange={onChange("phone")}
                />

                <div className="md:col-span-2 flex flex-col gap-2">
                  <label
                    htmlFor="project_type"
                    className="overline"
                  >
                    Project type
                  </label>
                  <select
                    id="project_type"
                    data-testid="contact-project-type"
                    value={form.project_type}
                    onChange={onChange("project_type")}
                    className="border border-hairline-dark bg-white text-navy font-heading text-sm py-3.5 px-4 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy"
                  >
                    {PROJECT_TYPES.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2 flex flex-col gap-2">
                  <label htmlFor="message" className="overline">
                    Project description *
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-message"
                    rows={6}
                    value={form.message}
                    onChange={onChange("message")}
                    placeholder="Brief scope description, contract reference, division, timeline…"
                    className={`border ${
                      errors.message ? "border-red-500" : "border-hairline-dark"
                    } bg-white text-navy text-sm py-3.5 px-4 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy placeholder:text-slate-400`}
                  />
                  {errors.message && (
                    <span className="text-xs text-red-600 font-mono uppercase tracking-wider">
                      {errors.message}
                    </span>
                  )}
                </div>

                <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-slate-500 font-mono uppercase tracking-[0.15em]">
                    * Required fields
                  </p>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-50"
                  >
                    {submitting ? "Submitting…" : "Submit Scope"}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, value, onChange, error, type = "text", required = false }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="overline">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={onChange}
        className={`border ${
          error ? "border-red-500" : "border-hairline-dark"
        } bg-white text-navy text-sm py-3.5 px-4 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy placeholder:text-slate-400`}
      />
      {error && (
        <span className="text-xs text-red-600 font-mono uppercase tracking-wider">
          {error}
        </span>
      )}
    </div>
  );
}
